import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Skills", href: "#skills" },
  { label: "Projects", href: "#projects" },
  { label: "Experience", href: "#experience" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar({ isDark, toggleTheme }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    const sections = NAV_LINKS.map((link) =>
      document.querySelector(link.href)
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(`#${entry.target.id}`);
          }
        });
      },
      {
        rootMargin: "-40% 0px -50% 0px",
        threshold: 0.1,
      }
    );

    sections.forEach((sec) => sec && observer.observe(sec));

    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);

    return () => {
      sections.forEach((sec) => sec && observer.unobserve(sec));
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
  }, [menuOpen]);

  const handleNav = (href) => {
    setMenuOpen(false);
    setActive(href);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* NAVBAR */}
      <motion.nav
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className={`fixed top-0 left-0 right-0 z-50 transition ${
          scrolled
            ? isDark
              ? "bg-[#0b0f19]/80 backdrop-blur-lg border-b border-white/10"
              : "bg-white/80 backdrop-blur-lg border-b border-gray-200"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">

          {/* LEFT SIDE */}
          <div className="flex items-center gap-3">

            {/* Logo */}
            <div
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="cursor-pointer font-semibold text-lg"
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                color: "var(--gold)",
              }}
            >
              NS<span className="text-gray-400">.</span>
            </div>

            {/* Theme Button */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="shrink-0 flex items-center gap-1 px-3 py-1 rounded-full border text-sm transition hover:scale-105"
              style={{
                borderColor: isDark ? "rgba(255,255,255,0.2)" : "#d1d5db",
                background: isDark
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.03)",
                color: isDark ? "#f9fafb" : "#111827",
              }}
            >
              {isDark ? "☀️" : "🌙"}
            </button>
          </div>

          {/* RIGHT SIDE */}
          <div className="flex items-center gap-4">

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  className={`group relative text-sm transition-colors duration-200 ${
                    active === link.href
                      ? "text-[color:var(--gold)] font-semibold"
                      : isDark
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-black"
                  }`}
                >
                  {link.label}

                  <span
                    className={`absolute left-0 -bottom-1 h-[2px] bg-[color:var(--gold)] transition-all duration-300 ${
                      active === link.href ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
           <button
  className="md:hidden text-xl"
  style={{ color: isDark ? "#ffffff" : "#000000" }}
  onClick={() => setMenuOpen(!menuOpen)}
>
  ☰
</button>
          </div>
        </div>
      </motion.nav>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 z-50 flex flex-col ${
              isDark ? "bg-[#0b0f19]" : "bg-white"
            }`}
          >
            {/* Close Button */}
            <button
              onClick={() => setMenuOpen(false)}
              className="absolute top-6 right-6 z-50 p-2 rounded-full backdrop-blur-md border shadow-md hover:scale-110"
              style={{
                background: isDark ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.9)",
                borderColor: isDark
                  ? "rgba(255,255,255,0.2)"
                  : "#e5e7eb",
                color: isDark ? "#ffffff" : "#111827",
              }}
            >
              ✕
            </button>

            <div className="mt-24 flex flex-col items-center gap-8 text-lg">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  className={`${
                    isDark ? "text-gray-200" : "text-gray-900"
                  }`}
                >
                  {link.label}
                </button>
              ))}

              {/* Theme inside mobile */}
              
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}